import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { WitsConcept } from "Backend";
import Flex from "components/Flex";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { browseConcepts, selectIsRuleSelected, toggleRuleSelected } from "../../redux/slices/lensEditor";
import { LensRule } from "../../generated/graphql";
import PCheckbox from "../../components/PCheckbox";
import { Link, Visibility } from "@mui/icons-material";
import truncateString from "../../helpers/truncateString";
import DuplicatesDialog from "./DuplicatesDialog";

const RuleReadOnly = (props: {
  rule: LensRule;
  readOnly?: boolean;
  setSelected: () => void;
  className?: string;
  concept: WitsConcept | undefined | null;
  deleteRule: () => void;
  linkToDuplicates?: boolean;
  hideConceptButton?: boolean;
}) => {
  const { rule, setSelected, concept, readOnly, linkToDuplicates, hideConceptButton } = props;
  const ruleId = rule.ruleId;
  const entityId = rule.entityQuery?.entityId;
  const textQuery = rule.textQuery;
  const dispatch = useAppDispatch();
  const isSelected = useAppSelector((st) => selectIsRuleSelected(st, ruleId));
  const [showDuplicates, setShowDuplicates] = useState<boolean>(false);
  const handleToggleSelect = React.useCallback(() => dispatch(toggleRuleSelected({ ruleId })), [dispatch, ruleId]);
  const mismatch = React.useMemo(
    () => concept?.umlsEntityType.toLowerCase() !== rule.entityQuery?.entityType.toLowerCase(),
    [concept?.umlsEntityType, rule.entityQuery?.entityType]
  );

  return (
    <>
      <Flex flexDirection="row" alignItems="center">
        {!readOnly && <PCheckbox label="" size="small" checked={isSelected} onChange={handleToggleSelect} />}

        <Typography sx={{ mr: 1, fontSize: "0.5rem" }}>id {rule.ruleId}</Typography>
        {!readOnly && (
          <IconButton size="small" onClick={setSelected}>
            <EditIcon />
          </IconButton>
        )}
        {linkToDuplicates && (
          <Tooltip title="Click to see duplicates" arrow>
            <IconButton size="small" onClick={() => setShowDuplicates(true)}>
              <Link />
            </IconButton>
          </Tooltip>
        )}
        {!readOnly && (
          <IconButton size="small" onClick={props.deleteRule}>
            <DeleteIcon />
          </IconButton>
        )}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              sx={{
                mr: 1,
                fontSize: "0.6rem",
                // start elipsis after 50 char
                maxWidth: "50ch",
              }}>
              {rule.category} -{">"} {rule.subtype} -{">"} {rule.description}
            </Typography>
          </Box>
          {rule.entityQuery && concept && (
            <>
              {mismatch && (
                <Typography variant="body2">
                  <ErrorIcon /> Entity type {rule.entityQuery.entityType} does not match {concept?.umlsEntityType}
                </Typography>
              )}
              {!hideConceptButton && (
                <Button
                  size="small"
                  sx={{ fontSize: "0.5rem" }}
                  color="info"
                  startIcon={<Visibility />}
                  onClick={() => dispatch(browseConcepts({ concepts: [concept] }))}>
                  {truncateString(concept.canonicalName, 40)} {entityId}
                </Button>
              )}
            </>
          )}
        </Box>

        {rule.entityQuery && !concept && (
          <>
            <Typography>{entityId}</Typography>
            <Typography variant="body2">
              <ErrorIcon /> Entity Not Found!
            </Typography>
          </>
        )}
        {textQuery && (
          <>
            <Typography variant="body2" sx={{ mr: 4 }}>
              Text
            </Typography>
            <Typography variant="body2">{textQuery.text}</Typography>
          </>
        )}
      </Flex>
      <DuplicatesDialog rule={rule} open={showDuplicates} close={() => setShowDuplicates(false)} />
    </>
  );
};

export const ReadOnlyMemo = React.memo(RuleReadOnly);
